import React, { useState, useEffect } from 'react';
import { User, Organization, UserRole } from '../types';
import { getOrganizations } from '../services/apiService';
import { LoadingSpinner } from './icons';

interface UserModalProps {
    user?: User | null;
    currentUser: User;
    onClose: () => void;
    onSave: (data: Partial<User> & { password?: string }, userId: string | null) => Promise<void>;
}

const UserModal: React.FC<UserModalProps> = ({ user, currentUser, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: UserRole.TAXI_NETWORK,
        organizationId: '',
        isActive: true,
    });
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEditing = !!user;

    useEffect(() => {
        const fetchOrgs = async () => {
            const orgsData = await getOrganizations();
            setOrganizations(orgsData);
            if (!isEditing && orgsData.length > 0) {
                setFormData(prev => ({ ...prev, organizationId: orgsData[0].id }));
            }
        };
        fetchOrgs();

        if (isEditing && user) {
            setFormData({
                name: user.name,
                email: user.email,
                password: '',
                confirmPassword: '',
                role: user.role,
                organizationId: user.organizationId,
                isActive: user.isActive,
            });
        }
    }, [user, isEditing]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            if (formData.password || !isEditing) { // Validate if creating OR if password field is touched when editing
                if (formData.password !== formData.confirmPassword) {
                    throw new Error("Passwords do not match.");
                }
                if ((!isEditing || formData.password) && formData.password.length < 8) { // check length if creating, or if editing and password is not empty
                    throw new Error("Password must be at least 8 characters long.");
                }
            }
            
            const { confirmPassword, ...dataToSend } = formData;
            
            if (isEditing && !dataToSend.password) {
                delete (dataToSend as any).password;
            }

            await onSave(dataToSend, user?.id || null);
            onClose();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Super Admins can create any role. Platform Admins cannot create Super Admins.
    const availableRoles = Object.values(UserRole).filter(role => 
        currentUser.role === UserRole.SUPER_ADMIN || role !== UserRole.SUPER_ADMIN
    );

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                <form onSubmit={handleSubmit}>
                    <h3 className="text-xl leading-6 font-bold text-gray-900 mb-4">{isEditing ? 'Edit User' : 'Create New User'}</h3>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input name="name" type="text" required value={formData.name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email Address</label>
                            <input name="email" type="email" required value={formData.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                        </div>
                        
                        <div className="border-t pt-4 space-y-4">
                            <h4 className="text-sm font-semibold text-gray-600">{isEditing ? 'Reset Password' : 'Set Password'}</h4>
                             <div>
                                <label className="block text-sm font-medium text-gray-700">{isEditing ? 'New Password (optional)' : 'Password'}</label>
                                <input name="password" type="password" required={!isEditing} value={formData.password} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{isEditing ? 'Confirm New Password' : 'Confirm Password'}</label>
                                <input name="confirmPassword" type="password" required={!isEditing} value={formData.confirmPassword} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                        </div>

                        <div className="border-t pt-4 space-y-4">
                            <h4 className="text-sm font-semibold text-gray-600">Role & Organization</h4>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700">Role</label>
                                 <select name="role" value={formData.role} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md">
                                    {availableRoles.map(role => <option key={role} value={role}>{role}</option>)}
                                 </select>
                            </div>
                            <div>
                                 <label className="block text-sm font-medium text-gray-700">Taxi Network / Organization</label>
                                 <select name="organizationId" value={formData.organizationId} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md">
                                    <option value="org-super">System Administration</option>
                                    {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                                 </select>
                            </div>
                             <div className="flex items-center">
                                <input name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-primary focus:ring-primary-light border-gray-300 rounded"/>
                                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">User is Active</label>
                            </div>
                        </div>
                    </div>

                    {error && <p className="mt-3 text-sm text-red-600 p-2 bg-red-50 rounded">{error}</p>}
                    
                    <div className="items-center px-4 py-3 mt-4 -mx-5 -mb-5 border-t flex justify-end gap-3 bg-gray-50 rounded-b-md">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isLoading} className="px-4 py-2 bg-primary text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-light disabled:bg-gray-400 flex items-center">
                            {isLoading && <LoadingSpinner className="w-5 h-5 mr-2" />}
                            {isEditing ? 'Save Changes' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserModal;